import { ActionIcon, Group, Loader, Text } from "@mantine/core"
import { useHover } from "@mantine/hooks"
import { showNotification } from "@mantine/notifications"
import {
  IconCaretDownFilled,
  IconCaretRightFilled,
  IconFolder,
} from "@tabler/icons-react"
import { MouseEventHandler, useState } from "react"

import { ROOT_FOLDER_ID } from "@costory/shared/const"

import { useDnDContext } from "@costory/front/components/FileExplorer/DnDContext"
import { ROW_HEIGHT } from "@costory/front/components/FileExplorer/FileListItem"
import { FolderNodeProps } from "@costory/front/components/FileExplorer/TreeExplorer"
import { useMoveFileMutation } from "@costory/front/queries/folders"

export const DefaultFolderNode = ({
  expanded,
  selected,
  node: {
    value,
    label,
    nodeProps: { onSelect, onDoubleClick, templateFolder, ...nodeProps },
  },
  elementProps,
}: FolderNodeProps) => {
  const { ref, hovered } = useHover()
  const { draggedFile } = useDnDContext()
  const { mutateAsync: moveFile, isPending } = useMoveFileMutation()
  const [isDragHover, setIsDragHover] = useState(false)

  const handleDoubleClick: MouseEventHandler<HTMLDivElement> = (e) => {
    e.stopPropagation()
    onDoubleClick?.(e)
  }

  const handleDrop: React.DragEventHandler<HTMLDivElement> = async (e) => {
    e.preventDefault()
    if (!draggedFile || draggedFile?.templateFolder || templateFolder) return

    try {
      await moveFile({
        fileId: draggedFile.id,
        type: draggedFile.type,
        folderId: value,
      })
      showNotification({
        title: "Success",
        message: "File moved successfully",
        color: "green",
      })
    } catch (e) {
      showNotification({
        title: "Error",
        message: "Failed to move file",
        color: "red",
      })
      console.error(e)
    }

    setIsDragHover(false)
  }
  const handleDragOver: React.DragEventHandler<HTMLDivElement> = (e) => {
    if (!templateFolder) {
      e.preventDefault()
    }
  }

  const handleDragEnter = () => {
    if (!templateFolder) {
      setIsDragHover(true)
    }
  }

  return (
    <Group
      ref={ref}
      h={ROW_HEIGHT}
      {...elementProps}
      justify="space-between"
      draggable={false}
      onClick={onSelect}
      onDoubleClick={handleDoubleClick}
      styles={{
        root: {
          borderRadius: 8,
          cursor: templateFolder ? "not-allowed" : "pointer",
          opacity: templateFolder ? 0.8 : 1,
        },
      }}
      bg={
        selected ? "surfaceSelected" : hovered ? "surfaceHover" : "transparent"
      }
      mb={4}
      bd={isDragHover ? "1px dashed black" : "none"}
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={() => setIsDragHover(false)}
      onDrop={handleDrop}
    >
      <Group gap={2} justify="space-between">
        <Group gap={8}>
          <ActionIcon
            disabled={value === ROOT_FOLDER_ID}
            bg="transparent"
            onClick={nodeProps.onClickExpand}
            size="sm"
            c={
              selected
                ? "textPrimary"
                : hovered
                  ? "textSecondary"
                  : "textPrimary"
            }
          >
            {isPending ? (
              <Loader size="sm" type="dots" />
            ) : expanded || nodeProps.shouldShowCreateFolderInput ? (
              <IconCaretDownFilled />
            ) : (
              <IconCaretRightFilled />
            )}
          </ActionIcon>
          <IconFolder size={20} />
        </Group>
        <Text mt={3} fw="bold">
          {label}
        </Text>
      </Group>
    </Group>
  )
}
